import React, { useState, useMemo, useEffect } from 'react';
import { X, Plus, Trash2, Loader2, IndianRupee } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { QuotationPreviewModal } from './quotation Preview';
import { budgetQuotationService } from '../services/budgetQuotationService';
import toast from 'react-hot-toast';
import { teamService } from '../../verve-teams/services/teamService';
import { settingsTermService } from '../../verve-settings/services/settingsTermService';
import { useAuthStore } from '@/store/authStore';

// ─── Editable Defaults ── change these to update discount & GST across all quotations ───
const DEFAULT_DISCOUNT_TYPE = 'percentage'; // 'percentage' | 'fixed'
const DEFAULT_DISCOUNT = 10;              // 10% discount
const DEFAULT_GST_TYPE = 'percentage';    // 'percentage' | 'fixed'
const DEFAULT_GST = 22;                   // 22% GST
// ─────────────────────────────────────────────────────────────────────────────────────────

export const InitialQuotationModal = ({ isOpen, onClose, contact, onSaved, existingData }) => {
  const [rows, setRows] = useState([
    { particulars: '', design: '', colourType: '', warranty: '', additionalNotes: '', amount: '' }
  ]);
  const [designers, setDesigners] = useState([]);
  const [terms, setTerms] = useState([]);
  const [quotationDate, setQuotationDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [currentQuotationNo, setCurrentQuotationNo] = useState('');

  const [gstType, setGstType] = useState(DEFAULT_GST_TYPE);
  const [gstValue, setGstValue] = useState(DEFAULT_GST);
  const [discountType, setDiscountType] = useState(DEFAULT_DISCOUNT_TYPE);
  const [discountValue, setDiscountValue] = useState(DEFAULT_DISCOUNT);

  const { user } = useAuthStore();
  // Logged-in user's display name (used as default Contact Ref)
  const loggedInUserName = user?.name || user?.firstName || 'Unknown';

  const [customContactRef, setCustomContactRef] = useState('');
  const [customDesigner, setCustomDesigner] = useState(contact?.assignee?.name || contact?.telecaller?.name || 'Unassigned');
  const [customProjectName, setCustomProjectName] = useState('');
  const [customSite, setCustomSite] = useState('');

  useEffect(() => {
    if (isOpen) {
      const defaultFullName = existingData?.projectName || existingData?.clientName || contact?.customerName || `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() || 'Unknown Client';
      const defaultSite = existingData?.site || `${contact?.city || ''}, ${contact?.state || ''}`.replace(/^,\s*|,\s*$/g, '') || '-';
      setCustomProjectName(defaultFullName);
      setCustomSite(defaultSite);

      const isRealQuotation = existingData && existingData.quotationNo && existingData.quotationNo !== '-';

      if (isRealQuotation) {
        setCurrentQuotationNo(existingData.quotationNo || '');
        const savedRef = existingData.contactRef || '';
        const refSource = contact?.referenceType || contact?.leadId?.referenceType || (contact?.source && contact.source !== 'Manual' ? contact.source : '') || existingData?.contactObj?.referenceType || '-';
        if (!savedRef || /^[a-fA-F0-9]{24}$/.test(savedRef) || savedRef === existingData?.contactObj?.customId || savedRef === contact?.customId || savedRef === String(contact?._id)) {
          setCustomContactRef(refSource);
        } else {
          setCustomContactRef(savedRef);
        }
        
        setCustomDesigner(existingData.referenceDesigner || loggedInUserName);

        if (existingData.rows && existingData.rows.length > 0) {
          setRows(existingData.rows.map(r => ({
            particulars: r.name || r.particulars || '',
            design: r.design || '',
            colourType: r.colour || r.colourType || '',
            warranty: r.warranty || '',
            additionalNotes: r.additionalNotes || '',
            amount: String(r?.amount ?? '').replace(/[^0-9.]/g, '') || ''
          })));
        } else {
          setRows([{ particulars: '', design: '', colourType: '', warranty: '', additionalNotes: '', amount: '' }]);
        }

        if (existingData.totals) {
          const discountStr = String(existingData.totals?.discount ?? '0');
          setDiscountType('fixed');
          setDiscountValue(parseFloat(discountStr.replace(/[^0-9.]/g, '')) || 0);
        }
      } else {
        // Reset defaults for a new quotation
        setRows([{ particulars: '', design: '', colourType: '', warranty: '', additionalNotes: '', amount: '' }]);
        setDiscountType('percentage');
        setDiscountValue(0);
        // New quotation: Contact Ref = Reference Source (Walk In etc.); Designer = logged-in user preparing
        const refSource = contact?.referenceType || contact?.leadId?.referenceType || (contact?.source && contact.source !== 'Manual' ? contact.source : '') || '-';
        setCustomContactRef(refSource);
        setCustomDesigner(loggedInUserName);

        budgetQuotationService.getNextQuotationNo('initial')
          .then(res => {
            if (res && res.data && res.data.quotationNo) {
              setCurrentQuotationNo(res.data.quotationNo);
            }
          })
          .catch(err => console.error("Failed to fetch next quotation no:", err));
      }

      // Fetch all team members for Prepared by dropdown
      teamService.getEmployees({ limit: 100 })
        .then(res => setDesigners(res?.data || []))
        .catch(err => console.error("Failed to fetch team members:", err));
        
      // Fetch Terms
      settingsTermService.getTerms()
        .then(res => {
          const fetchedTerms = Array.isArray(res?.data) ? res.data : [];
          const initialTerms = fetchedTerms.filter(t => t.status === 'Active' && t.category === 'Initial Quote');
          setTerms(initialTerms.length > 0 ? initialTerms : fetchedTerms.filter(t => t.status === 'Active' && !t.isRelatedToAppliances));
        })
        .catch(err => console.error("Failed to fetch terms:", err));
    }
  }, [isOpen, existingData, contact]);

  // Form totals
  const subTotal = useMemo(() => {
    return rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  }, [rows]);

  const discountAmount = discountType === 'percentage' 
    ? (subTotal * (Number(discountValue) || 0) / 100) 
    : (Number(discountValue) || 0);
  const taxableAmount = Math.max(0, subTotal - discountAmount);
  
  const gstAmount = gstType === 'percentage'
    ? (taxableAmount * (Number(gstValue) || 0) / 100)
    : (Number(gstValue) || 0);

  const grandTotal = taxableAmount + gstAmount;

  const formatCurrency = (val) => Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleAddRow = () => {
    setRows([...rows, { particulars: '', design: '', colourType: '', warranty: '', additionalNotes: '', amount: '' }]);
  };

  const handleRemoveRow = (index) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleRowChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  const handleSubmitQuotation = async (pdfBlob) => {
    const hasValidItem = rows.some(r => {
      const name = (r.particulars || '').trim();
      const amt = Number(r.amount);
      return name && amt > 0;
    });
    if (!hasValidItem) {
      toast.error('Add at least one item with a name and amount greater than 0.');
      return;
    }
    try {
      setIsSubmitting(true);

      const dataToSave = {
        contactId: contact?._id || existingData?.clientId || existingData?.leadId,
        projectName: customProjectName,
        clientName: customProjectName,
        site: customSite,
        contactRef: customContactRef,
        date: quotationDate.split('-').reverse().join('/'),
        quotationNo: currentQuotationNo || `VK/INT/${Date.now().toString().slice(-6)}`,
        referenceDesigner: customDesigner,
        rows: rows.filter(r => r.particulars.trim() !== '').map(r => ({
          name: r.particulars || '',
          design: r.design || '',
          type: '',
          colour: r.colourType || '',
          warranty: r.warranty || '',
          additionalNotes: r.additionalNotes || '',
          amount: r.amount?.toString() || '0',
          details: [],
          isHorizontal: false
        })),
        totals: {
          total: `₹${formatCurrency(subTotal)}`,
          discount: `₹${formatCurrency(discountAmount)}`,
          gst: `₹${formatCurrency(gstAmount)}`,
          grandTotal: `₹${formatCurrency(grandTotal)}`,
          discountType,
          discountValue,
          discountLabel: discountValue && Number(discountValue) !== 0 ? (discountType === 'percentage' ? `(${discountValue}%)` : `(₹${formatCurrency(discountValue)})`) : '',
          gstType,
          gstValue,
          gstLabel: gstValue && Number(gstValue) !== 0 ? (gstType === 'percentage' ? `(${gstValue}%)` : `(₹${formatCurrency(gstValue)})`) : ''
        },
        status: 'Approved', // Bypass approvals
        isInitial: true
      };

      let res;
      let savedId;
      if (existingData && existingData._id) {
        // If editing an existing quotation, update it via PUT request (assuming budgetQuotationService supports update, otherwise just save it which might create duplicate. Let's assume we can update or the backend saves with same ID)
        // Since budgetQuotationService.saveQuotation is POST, we might need a specific update if it exists. But budgetQuotationService POST handles updates if ID is provided?
        dataToSave._id = existingData._id;
        res = await budgetQuotationService.saveQuotation(dataToSave);
        savedId = existingData._id;
      } else {
        res = await budgetQuotationService.saveQuotation(dataToSave);
        savedId = res.data._id;
      }

      await budgetQuotationService.uploadPdf(savedId, pdfBlob, 'Approved');

      toast.success('Initial Quotation generated and saved successfully!');
      setShowPreview(false);
      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit initial quotation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const quotationData = {
    projectName: customProjectName,
    clientName: customProjectName,
    site: customSite,
    contactRef: customContactRef,
    date: quotationDate.split('-').reverse().join('/'),
    quotationNo: currentQuotationNo || `VK/INT/${Date.now().toString().slice(-6)}`,
    designer: customDesigner,
    isInitial: true,
    rooms: [
      {
        roomName: 'INITIAL QUOTATION',
      items: rows.filter(r => r.particulars.trim() !== '').map(r => ({
        name: r.particulars,
        design: r.design,
        type: '',
        colour: r.colour || r.colourType,
        vendor: r.vendor || r.colourType || "",
        warranty: r.warranty,
        additionalNotes: r.additionalNotes,
        amount: r.amount
      }))
      }
    ],
    totals: {
      total: `₹${formatCurrency(subTotal)}`,
      discount: `₹${formatCurrency(discountAmount)}`,
      gst: `₹${formatCurrency(gstAmount)}`,
      grandTotal: `₹${formatCurrency(grandTotal)}`,
      discountType,
      discountValue,
      discountLabel: discountValue && Number(discountValue) !== 0 ? (discountType === 'percentage' ? `(${discountValue}%)` : `(₹${formatCurrency(discountValue)})`) : '',
      gstType,
      gstValue,
      gstLabel: gstValue && Number(gstValue) !== 0 ? (gstType === 'percentage' ? `(${gstValue}%)` : `(₹${formatCurrency(gstValue)})`) : ''
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[990] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[90vh] overflow-hidden relative">

          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-slate-50">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Create Initial Quotation</h2>
              {/* <p className="text-sm text-slate-500 mt-1">Add items quickly to generate a simple initial quotation.</p> */}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm mb-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2 border-b border-slate-100 pb-2">Client Details</h3>
              
              {/* Top Row: Verve ID — commented out, not required */}
              {/* <div className="mb-2 flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-700">Verve ID:</span>
                <span className="text-sm font-medium text-brand-blue">{contact?.customId || '-'}</span>
              </div> */}

              {/* Bottom Row: Details and Editable Info */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
                  <p className="text-sm font-normal text-slate-800">{contact?.customerName || `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Site Location</label>
                  <p className="text-sm font-normal text-slate-800">{`${contact?.city || ''}, ${contact?.state || ''}`.replace(/^,\s*|,\s*$/g, '') || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                  <p className="text-sm font-normal text-slate-800">{contact?.phoneNumber || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                  <p className="text-sm font-normal text-slate-800">{contact?.email || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Project ID</label>
                  <p className="text-sm font-normal text-slate-800">{contact?.customId || '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Prepared by</label>
                  <select 
                    className="w-full text-sm border border-slate-300 bg-white rounded-md py-1.5 px-3 focus:ring-brand-blue focus:border-brand-blue transition-colors"
                    value={customDesigner}
                    onChange={(e) => setCustomDesigner(e.target.value)}
                  >
                    <option value="">-- Select Member --</option>
                    {designers.map(d => (
                      <option key={d._id} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Date of Quotation</label>
                  <input
                    type="date"
                    className="w-full text-sm border border-slate-300 bg-white rounded-md py-1.5 px-3 focus:ring-brand-blue focus:border-brand-blue transition-colors"
                    value={quotationDate}
                    onChange={(e) => setQuotationDate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-2">
                <h3 className="text-sm font-semibold text-slate-800">Quotation Items</h3>
                <Button variant="secondary" size="sm" onClick={handleAddRow} className="flex items-center gap-1">
                  <Plus size={16} /> Add Item
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 font-medium w-[25%]">Particulars</th>
                      <th className="px-3 py-2 font-medium w-[15%]">Design</th>
                      <th className="px-3 py-2 font-medium w-[15%]">Color / Type</th>
                      <th className="px-3 py-2 font-medium w-[12%]">Warranty</th>
                      <th className="px-3 py-2 font-medium w-[20%]">Additional Notes</th>
                      <th className="px-3 py-2 font-medium w-[10%]">Amount (₹)</th>
                      <th className="px-3 py-2 font-medium w-[30px]"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((row, index) => (
                      <tr key={index}>
                        <td className="p-2">
                          <textarea
                            rows={3}
                            className="w-full text-sm border border-slate-300 bg-white rounded-md p-2 focus:ring-brand-blue focus:border-brand-blue resize-y transition-colors shadow-sm"
                            placeholder="e.g. Wardrobe, Modular Kitchen..."
                            value={row.particulars}
                            onChange={(e) => handleRowChange(index, 'particulars', e.target.value)}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="text"
                            className="w-full text-sm border border-slate-300 bg-white rounded-md p-2 focus:ring-brand-blue focus:border-brand-blue transition-colors shadow-sm"
                            placeholder="Design Details"
                            value={row.design}
                            onChange={(e) => handleRowChange(index, 'design', e.target.value)}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="text"
                            className="w-full text-sm border border-slate-300 bg-white rounded-md p-2 focus:ring-brand-blue focus:border-brand-blue transition-colors shadow-sm"
                            placeholder="Colour / Material"
                            value={row.colourType}
                            onChange={(e) => handleRowChange(index, 'colourType', e.target.value)}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="text"
                            className="w-full text-sm border border-slate-300 bg-white rounded-md p-2 focus:ring-brand-blue focus:border-brand-blue transition-colors shadow-sm"
                            placeholder="e.g. 5 Years"
                            value={row.warranty}
                            onChange={(e) => handleRowChange(index, 'warranty', e.target.value)}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="text"
                            className="w-full text-sm border border-slate-300 bg-white rounded-md p-2 focus:ring-brand-blue focus:border-brand-blue transition-colors shadow-sm"
                            placeholder="Notes..."
                            value={row.additionalNotes}
                            onChange={(e) => handleRowChange(index, 'additionalNotes', e.target.value)}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="number"
                            className="w-full text-sm border border-slate-300 bg-white rounded-md p-2 focus:ring-brand-blue focus:border-brand-blue transition-colors shadow-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder="0"
                            value={row.amount}
                            onChange={(e) => handleRowChange(index, 'amount', e.target.value)}
                          />
                        </td>
                        <td className="p-2 text-center">
                          {rows.length > 1 && (
                            <button
                              onClick={() => handleRemoveRow(index)}
                              className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals Section */}
              <div className="mt-6 flex flex-col md:flex-row justify-between gap-6">
                {/* Terms on the left */}
                <div className="flex-1 bg-white p-4 rounded-xl border border-slate-200">
                  <h4 className="text-xs font-bold text-slate-700 uppercase mb-3 border-b border-slate-100 pb-2">Terms & Conditions</h4>
                  <div className="text-[11px] text-slate-600 space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    {terms.length > 0 ? terms.map((t, i) => (
                      <div key={i} className="mb-3">
                        <div className="font-semibold text-slate-800 mb-1">{i + 1}) {t.title || "TERMS"}</div>
                        {t.content && <div dangerouslySetInnerHTML={{ __html: t.content }} className="prose prose-sm prose-slate max-w-none text-[11px] leading-snug [&>p]:mb-1 [&>ul]:mt-1 [&>ul]:mb-1" />}
                      </div>
                    )) : (
                      <p className="italic text-slate-400">Standard terms and conditions apply. Configure these in the Settings page.</p>
                    )}
                  </div>
                </div>

                <div className="w-full md:w-[320px] bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3 shrink-0">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500">Sub Total:</span>
                    <span className="font-semibold text-slate-800">₹{formatCurrency(subTotal)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500">Discount:</span>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center bg-white border border-slate-200 rounded text-[11px] overflow-hidden">
                        <button 
                          className={`px-2 py-1 ${discountType === 'percentage' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                          onClick={() => setDiscountType('percentage')}
                        >%</button>
                        <button 
                          className={`px-2 py-1 border-l border-slate-200 ${discountType === 'fixed' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                          onClick={() => setDiscountType('fixed')}
                        >₹</button>
                      </div>
                      <input
                        type="number"
                        className="w-24 text-right text-sm border border-slate-300 bg-white rounded-md py-1 px-2 h-8 focus:ring-brand-blue focus:border-brand-blue transition-colors shadow-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end text-xs text-red-500">
                    - ₹{formatCurrency(discountAmount)}
                  </div>
                  <div className="flex justify-between items-center text-sm border-t border-slate-100 pt-2 mt-2">
                    <span className="text-slate-500 font-medium">Taxable Amount:</span>
                    <span className="font-semibold text-slate-700">₹{formatCurrency(taxableAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-2">
                    <span className="text-slate-500">GST:</span>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center bg-white border border-slate-200 rounded text-[11px] overflow-hidden">
                        <button 
                          className={`px-2 py-1 ${gstType === 'percentage' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                          onClick={() => setGstType('percentage')}
                        >%</button>
                        <button 
                          className={`px-2 py-1 border-l border-slate-200 ${gstType === 'fixed' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                          onClick={() => setGstType('fixed')}
                        >₹</button>
                      </div>
                      <input
                        type="number"
                        className="w-24 text-right text-sm border border-slate-300 bg-white rounded-md py-1 px-2 h-8 focus:ring-brand-blue focus:border-brand-blue transition-colors shadow-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        value={gstValue}
                        onChange={(e) => setGstValue(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end text-xs text-slate-500">
                    + ₹{formatCurrency(gstAmount)}
                  </div>
                  <div className="pt-3 border-t border-slate-200 flex justify-between items-center">
                    <span className="font-bold text-slate-800">Final Amount:</span>
                    <span className="text-lg font-bold text-brand-blue">₹{formatCurrency(grandTotal)}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 bg-slate-50 border-t border-slate-200 shrink-0 rounded-b-xl">
            <Button variant="ghost" onClick={onClose} className="px-6">Cancel</Button>
            <Button
              variant="orange"
              onClick={() => setShowPreview(true)}
              disabled={rows.length === 0 || !rows[0].particulars}
              className="px-6 shadow-md"
            >
              Preview & Generate
            </Button>
          </div>
        </div>
      </div>

      {showPreview && (
        <QuotationPreviewModal
          data={quotationData}
          onClose={() => setShowPreview(false)}
          onSubmit={handleSubmitQuotation}
        />
      )}
    </>
  );
};
